import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import clsx from 'clsx';
import { api } from '@/api/client';
import { extractErrorMessage } from '@/api/errors';
import { useAuth } from '@/auth/useAuth';
import {
  LIVE_RELEVANT_ROLES,
  ROLE_REQUIRED_DOCS,
  USER_REQUIRED_DOCS,
  isUserVerified,
} from '@/constants/kyc-requirements';
import { VerifiedBadge } from '@/components/VerifiedBadge';
import {
  ExtendedUserMetadata,
  KycDocument,
  RoleKycDocument,
  TermsAcceptanceStatus,
  UserAddress,
  UserDetail,
  UserKycResponse,
  UserRole,
} from '@/types';

function formatDate(iso: string | null | undefined) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

type Tab = 'overview' | 'kyc';

interface DeletionState {
  has_request: boolean;
  request?: {
    id: number;
    purge_state: 'pending' | 'failed' | 'purged' | 'restored';
    purge_at: string | null;
    reason_category: string;
    reason_text: string | null;
    requested_via: string;
    acting_admin_id: number | null;
    purge_attempts: number | null;
    last_error: string | null;
    reminder_sent_at: string | null;
    restored_at: string | null;
    purged_at: string | null;
    swept_amount: string | null;
    created_at: string | null;
  };
  history?: {
    action: string;
    actor_user_id: number | null;
    details: Record<string, unknown> | null;
    created_at: string;
  }[];
}

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = useAuth();
  const canSuspend = hasPermission('users.suspend');
  const canViewKyc = hasPermission('kyc.view');
  const canManageDeletions = hasPermission('account_deletion.manage');

  const [detail, setDetail] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState(false);
  const [tab, setTab] = useState<Tab>('overview');
  const [deletion, setDeletion] = useState<DeletionState | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<UserDetail>(`/back-office/users/${id}`);
      setDetail(res.data);
      // Separate call, and deliberately non-fatal: a user page must still
      // render if the deletion service is unreachable.
      try {
        const deletionRes = await api.get<DeletionState>(
          `/back-office/users/${id}/deletion-state`
        );
        setDeletion(deletionRes.data);
      } catch {
        setDeletion(null);
      }
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'Failed to load user'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const restoreAccount = async () => {
    if (!id || !canManageDeletions) return;
    const note = window.prompt(
      'Restore this account? Give a reason for the audit trail:'
    );
    if (note === null) return;
    setActing(true);
    try {
      await api.post(`/back-office/users/${id}/restore-account`, {
        note: note || undefined,
      });
      await load();
    } catch (err) {
      window.alert(extractErrorMessage(err, 'Restore failed'));
    } finally {
      setActing(false);
    }
  };

  const forceDeleteAccount = async () => {
    if (!id || !canManageDeletions) return;
    const reason = window.prompt(
      "Close this account on the user's behalf, overriding the eligibility blockers.\n\nWhy? (recorded in the audit trail)"
    );
    if (reason === null) return;
    if (
      !window.confirm(
        'This locks the account immediately and permanently deletes it after the grace period. Continue?'
      )
    ) {
      return;
    }
    setActing(true);
    try {
      await api.post(`/back-office/users/${id}/force-delete-account`, {
        reason_text: reason || undefined,
        // Explicit, because the server refuses without it — forcing past the
        // blockers has to be a deliberate act.
        override_blockers: true,
      });
      await load();
    } catch (err) {
      window.alert(extractErrorMessage(err, 'Could not delete the account'));
    } finally {
      setActing(false);
    }
  };

  const toggleSuspend = async () => {
    if (!detail || !canSuspend) return;
    const suspended = !!detail.user.suspended_at;
    const verb = suspended ? 'unsuspend' : 'suspend';
    if (
      !window.confirm(
        `${verb[0].toUpperCase()}${verb.slice(1)} ${detail.user.email}?`
      )
    ) {
      return;
    }
    let reason: string | undefined;
    if (!suspended) {
      reason = window.prompt('Reason (optional):') ?? undefined;
    }
    setActing(true);
    try {
      await api.post(
        `/back-office/users/${detail.user.id}/${verb}`,
        reason ? { reason } : {}
      );
      await load();
    } catch (err: unknown) {
      window.alert(extractErrorMessage(err, `Failed to ${verb}`));
    } finally {
      setActing(false);
    }
  };

  if (loading) {
    return <div className="px-8 py-6 text-slate-500">Loading…</div>;
  }

  if (error || !detail) {
    return (
      <div className="px-8 py-6">
        <Link to="/users" className="text-sm text-brand-600 hover:underline">
          ← Back to users
        </Link>
        <div className="mt-4 rounded bg-red-50 px-4 py-3 text-sm text-red-700">
          {error ?? 'User not found'}
        </div>
      </div>
    );
  }

  const { user, roles, extended_user, profile_image_url } = detail;
  const suspended = !!user.suspended_at;
  const fullName = `${user.first_name} ${user.last_name}`.trim();
  const userName = extended_user?.meta_data?.secondary_information?.user_name;
  const verified = isUserVerified({
    is_verified: user.is_verified,
    suspended_at: user.suspended_at,
    roles: roles.map((r) => ({
      role_type: r.role_type,
      role_verified: r.role_verified,
    })),
  });

  return (
    <div className="px-8 py-6">
      <Link to="/users" className="text-sm text-brand-600 hover:underline">
        ← Back to users
      </Link>

      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <Avatar
            src={profile_image_url}
            firstName={user.first_name}
            lastName={user.last_name}
          />
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-semibold text-slate-800">
              <span>{fullName || 'Unnamed user'}</span>
              {verified && <VerifiedBadge size="lg" />}
            </h1>
            {userName && (
              <div className="mt-0.5 text-sm text-slate-500">@{userName}</div>
            )}
            <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-slate-500">
              <span>{user.email}</span>
              <span>·</span>
              <span>{user.phone ?? 'No phone'}</span>
              <span>·</span>
              <span
                className={clsx(
                  'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
                  suspended
                    ? 'bg-red-100 text-red-700'
                    : user.is_verified
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-amber-100 text-amber-700'
                )}
              >
                {suspended
                  ? 'Suspended'
                  : user.is_verified
                    ? 'Active'
                    : 'Unverified'}
              </span>
            </div>
          </div>
        </div>
        {canSuspend && (
          <button
            type="button"
            onClick={toggleSuspend}
            disabled={acting}
            className={clsx(
              'rounded border px-4 py-2 text-sm font-medium disabled:opacity-50',
              suspended
                ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'
                : 'border-red-300 text-red-700 hover:bg-red-50'
            )}
          >
            {acting ? '…' : suspended ? 'Unsuspend user' : 'Suspend user'}
          </button>
        )}
      </div>

      {deletion?.has_request && deletion.request && (
        <div
          className={clsx(
            'mt-4 rounded border px-4 py-3 text-sm',
            deletion.request.purge_state === 'failed'
              ? 'border-red-300 bg-red-50 text-red-900'
              : deletion.request.purge_state === 'pending'
                ? 'border-amber-300 bg-amber-50 text-amber-900'
                : 'border-slate-200 bg-slate-50 text-slate-700'
          )}
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="font-medium">
                {deletion.request.purge_state === 'pending' &&
                  `Deletion requested — permanent on ${formatDate(
                    deletion.request.purge_at
                  )}`}
                {deletion.request.purge_state === 'failed' &&
                  'Deletion stalled — needs attention'}
                {deletion.request.purge_state === 'purged' &&
                  `Purged ${formatDate(deletion.request.purged_at)}`}
                {deletion.request.purge_state === 'restored' &&
                  `Restored ${formatDate(deletion.request.restored_at)}`}
              </div>
              <div className="mt-1">
                Reason: {deletion.request.reason_category.replace(/_/g, ' ')}
                {deletion.request.reason_text
                  ? ` — “${deletion.request.reason_text}”`
                  : ''}
              </div>
              <div className="mt-0.5 text-xs opacity-80">
                Requested {formatDate(deletion.request.created_at)} via{' '}
                {deletion.request.requested_via}
                {deletion.request.acting_admin_id
                  ? ` by admin #${deletion.request.acting_admin_id}`
                  : ''}
                {deletion.request.reminder_sent_at ? ' · reminder sent' : ''}
                {deletion.request.swept_amount
                  ? ` · ₦${deletion.request.swept_amount} forfeited`
                  : ''}
              </div>
              {deletion.request.last_error && (
                <div className="mt-1.5 text-xs font-medium">
                  {deletion.request.last_error}
                  {(deletion.request.purge_attempts ?? 0) > 0 &&
                    ` (${deletion.request.purge_attempts} attempt(s))`}
                </div>
              )}
            </div>
            {canManageDeletions &&
              deletion.request.purge_state === 'pending' && (
                <button
                  type="button"
                  onClick={restoreAccount}
                  disabled={acting}
                  className="rounded border border-emerald-300 bg-white px-3 py-1.5 text-sm font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                >
                  {acting ? '…' : 'Restore account'}
                </button>
              )}
          </div>

          {deletion.history && deletion.history.length > 0 && (
            <details className="mt-3">
              <summary className="cursor-pointer text-xs font-medium opacity-80">
                Audit trail ({deletion.history.length})
              </summary>
              <ul className="mt-2 space-y-1 text-xs opacity-90">
                {deletion.history.map((entry, index) => (
                  <li key={`${entry.action}-${index}`}>
                    <span className="font-mono">
                      {formatDate(entry.created_at)}
                    </span>{' '}
                    — {entry.action.replace(/_/g, ' ')}
                    {entry.actor_user_id
                      ? ` (by #${entry.actor_user_id})`
                      : ' (system)'}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {/* Only offered when the user has NOT already asked: this is the escape
          hatch for someone who cannot clear a blocker themselves, not a
          general-purpose delete button. */}
      {canManageDeletions &&
        !user.deleted_at &&
        !deletion?.request?.purge_state && (
          <div className="mt-4 rounded border border-slate-200 bg-white px-4 py-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm text-slate-600">
                <div className="font-medium text-slate-800">
                  Close this account on the user&apos;s behalf
                </div>
                <div className="mt-0.5 text-xs">
                  For a user who asked to delete but cannot clear a blocker
                  themselves. Starts the same {'30'}-day grace period.
                </div>
              </div>
              <button
                type="button"
                onClick={forceDeleteAccount}
                disabled={acting}
                className="rounded border border-red-300 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                {acting ? '…' : 'Delete account (override blockers)'}
              </button>
            </div>
          </div>
        )}

      {suspended && (
        <div className="mt-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <div className="font-medium">
            Suspended {formatDate(user.suspended_at)}{' '}
            {user.suspended_by ? `by admin #${user.suspended_by}` : ''}
          </div>
          {user.suspension_reason && (
            <div className="mt-1 text-red-700">{user.suspension_reason}</div>
          )}
        </div>
      )}

      <div className="mt-6 border-b border-slate-200">
        <nav className="flex gap-6">
          <TabButton
            active={tab === 'overview'}
            onClick={() => setTab('overview')}
          >
            Overview
          </TabButton>
          {canViewKyc && (
            <TabButton active={tab === 'kyc'} onClick={() => setTab('kyc')}>
              KYC documents
            </TabButton>
          )}
        </nav>
      </div>

      {tab === 'overview' && (
        <>
          <OverviewTab
            user={user}
            roles={roles}
            extendedUser={extended_user}
            addresses={detail.addresses ?? []}
            terms={detail.terms_acceptance}
          />
          <WalletPayoutsSection userId={user.id} />
        </>
      )}

      {tab === 'kyc' && canViewKyc && (
        <KycTab userId={user.id} roles={roles} onRoleChange={load} />
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        '-mb-px border-b-2 px-1 py-3 text-sm font-medium transition-colors',
        active
          ? 'border-brand-500 text-brand-600'
          : 'border-transparent text-slate-500 hover:text-slate-700'
      )}
    >
      {children}
    </button>
  );
}

type DocScope = 'user' | 'role';

function KycTab({
  userId,
  roles,
  onRoleChange,
}: {
  userId: number;
  roles: UserRole[];
  onRoleChange: () => void;
}) {
  const { hasPermission } = useAuth();
  const canReview = hasPermission('kyc.review');

  const [data, setData] = useState<UserKycResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyDocId, setBusyDocId] = useState<string | null>(null);
  const [busyRoleId, setBusyRoleId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<UserKycResponse>(
        `/back-office/users/${userId}/kyc`
      );
      setData(res.data);
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'Failed to load KYC'));
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const approveDoc = async (scope: DocScope, docId: number) => {
    setBusyDocId(`${scope}:${docId}`);
    try {
      await api.post(`/back-office/kyc/${scope}-docs/${docId}/approve`);
      await load();
    } catch (err: unknown) {
      window.alert(extractErrorMessage(err, 'Failed to approve document'));
    } finally {
      setBusyDocId(null);
    }
  };

  const rejectDoc = async (scope: DocScope, docId: number) => {
    const reason = window.prompt(
      'Reason for rejection (visible to the user, min 3 chars):'
    );
    if (!reason || reason.trim().length < 3) {
      window.alert('A rejection reason of at least 3 characters is required.');
      return;
    }
    setBusyDocId(`${scope}:${docId}`);
    try {
      await api.post(`/back-office/kyc/${scope}-docs/${docId}/reject`, {
        reason: reason.trim(),
      });
      await load();
    } catch (err: unknown) {
      window.alert(extractErrorMessage(err, 'Failed to reject document'));
    } finally {
      setBusyDocId(null);
    }
  };

  const setRoleLive = async (roleId: number) => {
    if (
      !window.confirm('Make this role live? The profile will become active.')
    ) {
      return;
    }
    setBusyRoleId(roleId);
    try {
      await api.post(`/back-office/roles/${roleId}/approve`);
      await load();
      onRoleChange();
    } catch (err: unknown) {
      window.alert(extractErrorMessage(err, 'Failed to make role live'));
    } finally {
      setBusyRoleId(null);
    }
  };

  const revokeRole = async (roleId: number) => {
    if (
      !window.confirm(
        'Revoke this live role? The user will no longer appear active.'
      )
    ) {
      return;
    }
    setBusyRoleId(roleId);
    try {
      await api.post(`/back-office/roles/${roleId}/revoke`);
      await load();
      onRoleChange();
    } catch (err: unknown) {
      window.alert(extractErrorMessage(err, 'Failed to revoke role'));
    } finally {
      setBusyRoleId(null);
    }
  };

  if (loading) {
    return <div className="mt-6 text-sm text-slate-500">Loading KYC…</div>;
  }

  if (error) {
    return (
      <div className="mt-6 rounded bg-red-50 px-4 py-3 text-sm text-red-700">
        {error}
      </div>
    );
  }

  const userDocs = data?.user_documents ?? [];
  const roleDocs = data?.role_documents ?? [];

  // Map role docs by role_id so we can pair them with the roles[] from UserDetail
  // (which carries role.role_verified — the "live" flag).
  const docsByRoleId = roleDocs.reduce<Record<number, RoleKycDocument[]>>(
    (acc, doc) => {
      (acc[doc.role_id] ??= []).push(doc);
      return acc;
    },
    {}
  );

  // Helper: which required docs are missing approval, for a given role.
  const evaluateReadiness = (role: UserRole) => {
    const roleRequired = ROLE_REQUIRED_DOCS[role.role_type] ?? [];
    const userRequired = USER_REQUIRED_DOCS[role.role_type] ?? [];

    const roleApproved = new Set(
      (docsByRoleId[role.id] ?? [])
        .filter((d) => d.verification_status === 'approved')
        .map((d) => d.name)
    );
    const userApproved = new Set(
      userDocs
        .filter((d) => d.verification_status === 'approved')
        .map((d) => d.name)
    );

    const missingRole = roleRequired.filter((n) => !roleApproved.has(n));
    const missingUser = userRequired.filter((n) => !userApproved.has(n));

    const total = roleRequired.length + userRequired.length;
    const approved = total - missingRole.length - missingUser.length;
    return {
      total,
      approved,
      missing: [...missingRole, ...missingUser],
      ready: missingRole.length === 0 && missingUser.length === 0,
    };
  };

  return (
    <div className="mt-6 space-y-6">
      {userDocs.length > 0 && (
        <Section title="Personal KYC (user-scoped)">
          <DocList
            docs={userDocs}
            scope="user"
            canReview={canReview}
            busyDocId={busyDocId}
            onApprove={(id) => approveDoc('user', id)}
            onReject={(id) => rejectDoc('user', id)}
          />
        </Section>
      )}

      {roles.map((role) => {
        const docs = docsByRoleId[role.id] ?? [];
        const liveRelevant = LIVE_RELEVANT_ROLES.has(role.role_type);

        // For roles that aren't gated by go-live (individual, admin,
        // super_admin) AND have no submitted docs, there's nothing useful to
        // show — skip the card so the tab isn't cluttered.
        if (!liveRelevant && docs.length === 0) {
          return null;
        }

        const readiness = evaluateReadiness(role);
        const live = role.role_verified;
        const busy = busyRoleId === role.id;

        return (
          <section
            key={role.id}
            className="rounded border border-slate-200 bg-white p-5"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                  Role: {role.role_type}
                </h2>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                  {liveRelevant ? (
                    <>
                      <span
                        className={clsx(
                          'inline-flex rounded-full px-2 py-0.5 font-medium',
                          live
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-slate-200 text-slate-700'
                        )}
                      >
                        {live ? 'Live' : 'Not live'}
                      </span>
                      {readiness.total > 0 && (
                        <span className="text-slate-500">
                          {readiness.approved} / {readiness.total} required
                          documents approved
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-slate-500">
                      Auto-active — no admin approval needed for this role
                    </span>
                  )}
                </div>
                {liveRelevant && !live && readiness.missing.length > 0 && (
                  <div className="mt-2 text-xs text-amber-700">
                    Missing approvals: {readiness.missing.join(', ')}
                  </div>
                )}
              </div>

              {canReview && liveRelevant && (
                <div className="flex shrink-0 gap-2">
                  {live ? (
                    <button
                      type="button"
                      onClick={() => revokeRole(role.id)}
                      disabled={busy}
                      className="rounded border border-red-300 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                    >
                      {busy ? '…' : 'Revoke (take offline)'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setRoleLive(role.id)}
                      disabled={busy || !readiness.ready}
                      title={
                        readiness.ready
                          ? 'Activate this role'
                          : 'All required documents must be approved first'
                      }
                      className="rounded border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {busy ? '…' : 'Make role live'}
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="mt-4">
              <RoleDocList
                requiredNames={ROLE_REQUIRED_DOCS[role.role_type] ?? []}
                docs={docs}
                canReview={canReview}
                busyDocId={busyDocId}
                onApprove={(id) => approveDoc('role', id)}
                onReject={(id) => rejectDoc('role', id)}
              />
            </div>
          </section>
        );
      })}
    </div>
  );
}

interface DocListProps {
  docs: KycDocument[];
  scope: DocScope;
  canReview: boolean;
  busyDocId: string | null;
  onApprove: (id: number) => void;
  onReject: (id: number) => void;
}

function DocList({
  docs,
  scope,
  canReview,
  busyDocId,
  onApprove,
  onReject,
}: DocListProps) {
  return (
    <div className="space-y-2">
      {docs.map((d) => (
        <DocRow
          key={d.id}
          doc={d}
          scope={scope}
          canReview={canReview}
          busy={busyDocId === `${scope}:${d.id}`}
          onApprove={onApprove}
          onReject={onReject}
        />
      ))}
    </div>
  );
}

interface RoleDocListProps {
  requiredNames: string[];
  docs: RoleKycDocument[];
  canReview: boolean;
  busyDocId: string | null;
  onApprove: (id: number) => void;
  onReject: (id: number) => void;
}

/**
 * Renders the full required-doc checklist for a role, not just whatever has
 * been submitted. Every required doc (e.g. TIN) shows as a line item so admins
 * can always see its status; submitted ones are reviewable, the rest render as
 * "Not submitted". Any extra submitted docs outside the required set are still
 * listed so nothing is hidden.
 */
function RoleDocList({
  requiredNames,
  docs,
  canReview,
  busyDocId,
  onApprove,
  onReject,
}: RoleDocListProps) {
  const byName = new Map(docs.map((d) => [d.name, d]));
  const extraDocs = docs.filter((d) => !requiredNames.includes(d.name));

  if (requiredNames.length === 0 && docs.length === 0) {
    return (
      <div className="text-xs text-slate-500">
        No role-scoped documents submitted yet.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {requiredNames.map((name) => {
        const doc = byName.get(name);
        return doc ? (
          <DocRow
            key={`role:${doc.id}`}
            doc={doc}
            scope="role"
            canReview={canReview}
            busy={busyDocId === `role:${doc.id}`}
            onApprove={onApprove}
            onReject={onReject}
          />
        ) : (
          <MissingDocRow key={`missing:${name}`} name={name} />
        );
      })}
      {extraDocs.map((doc) => (
        <DocRow
          key={`role:${doc.id}`}
          doc={doc}
          scope="role"
          canReview={canReview}
          busy={busyDocId === `role:${doc.id}`}
          onApprove={onApprove}
          onReject={onReject}
        />
      ))}
    </div>
  );
}

function MissingDocRow({ name }: { name: string }) {
  return (
    <div className="rounded border border-dashed border-slate-300 px-3 py-3">
      <div className="flex items-center justify-between gap-3">
        <div className="font-medium text-slate-500">{name}</div>
        <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">
          Not submitted
        </span>
      </div>
    </div>
  );
}

function DocRow({
  doc,
  scope,
  canReview,
  busy,
  onApprove,
  onReject,
}: {
  doc: KycDocument;
  scope: DocScope;
  canReview: boolean;
  busy: boolean;
  onApprove: (id: number) => void;
  onReject: (id: number) => void;
}) {
  const status = doc.verification_status ?? 'pending';
  const statusClass =
    status === 'approved'
      ? 'bg-emerald-100 text-emerald-700'
      : status === 'rejected'
        ? 'bg-red-100 text-red-700'
        : 'bg-amber-100 text-amber-700';

  // Suppress unused-var when scope is only used by the parent.
  void scope;

  const documentNumber = getDocumentNumber(doc);
  // True when neither a certificate file nor a document number is on the
  // record — e.g. legacy TIN rows whose number was dropped before the
  // backend metadata→meta_info fallback landed. Surfaced as a warning so
  // admins approving such rows know they're doing so without source data.
  const noSourceData = !doc.file_id && !documentNumber;

  return (
    <div className="rounded border border-slate-200 px-3 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-slate-800">{doc.name}</div>
          {documentNumber && (
            <div className="mt-1 font-mono text-sm text-slate-700">
              {documentNumber}
            </div>
          )}
          <div className="mt-0.5 text-xs text-slate-500">
            Submitted {formatDate(doc.created_at)}
            {doc.verified_at && ` · Reviewed ${formatDate(doc.verified_at)}`}
          </div>
          {noSourceData && (
            <div className="mt-1 text-xs text-amber-700">
              No certificate or number on file — approving will mark this
              verified without source data. Consider rejecting to request a
              resubmission.
            </div>
          )}
          {doc.rejection_reason && status === 'rejected' && (
            <div className="mt-1 text-xs text-red-700">
              Rejection reason: {doc.rejection_reason}
            </div>
          )}
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <span
            className={clsx(
              'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
              statusClass
            )}
          >
            {status}
          </span>
          {doc.file_url ? (
            <a
              href={doc.file_url}
              target="_blank"
              rel="noreferrer noopener"
              className="rounded border border-slate-300 px-2 py-0.5 text-xs text-slate-700 hover:bg-slate-50"
            >
              Open file
            </a>
          ) : noSourceData ? (
            <span className="text-xs text-amber-600">No data</span>
          ) : null}
          {canReview && (
            <>
              <button
                type="button"
                onClick={() => onApprove(doc.id)}
                disabled={busy || status === 'approved'}
                title={
                  noSourceData
                    ? 'No file or number on this record — approve only if verified out-of-band'
                    : undefined
                }
                className="rounded border border-emerald-300 px-2 py-0.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {busy ? '…' : 'Approve'}
              </button>
              <button
                type="button"
                onClick={() => onReject(doc.id)}
                disabled={busy || status === 'rejected'}
                className="rounded border border-red-300 px-2 py-0.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {busy ? '…' : 'Reject'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Pulls the number/identifier admins need to eyeball-verify items like BVN,
// NIN, or TIN out of meta_info. Mobile clients write it under document_number
// (with a few legacy aliases like bvn_number/nin_number).
function getDocumentNumber(doc: KycDocument): string | null {
  const meta = doc.meta_info;
  if (!meta || typeof meta !== 'object') return null;
  const keys = [
    'document_number',
    'bvn_number',
    'nin_number',
    'tin_number',
    'number',
  ];
  for (const k of keys) {
    const v = (meta as Record<string, unknown>)[k];
    if (typeof v === 'string' && v.trim().length > 0) return v.trim();
    if (typeof v === 'number') return String(v);
  }
  return null;
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded border border-slate-200 bg-white p-5">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
        {title}
      </h2>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function DetailRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="text-right text-slate-800">{value}</span>
    </div>
  );
}

// --- Overview tab ---------------------------------------------------------

function Avatar({
  src,
  firstName,
  lastName,
}: {
  src: string | null;
  firstName: string;
  lastName: string;
}) {
  const initials =
    `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase() || '?';

  if (src) {
    return (
      <img
        src={src}
        alt={`${firstName} ${lastName}`}
        className="h-20 w-20 shrink-0 rounded-full border border-slate-200 object-cover"
        onError={(e) => {
          (e.target as HTMLImageElement).style.display = 'none';
        }}
      />
    );
  }

  return (
    <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-brand-700 text-2xl font-semibold text-white">
      {initials}
    </div>
  );
}

interface OverviewTabProps {
  user: UserDetail['user'];
  roles: UserRole[];
  extendedUser: UserDetail['extended_user'];
  addresses: UserAddress[];
  terms: TermsAcceptanceStatus;
}

function OverviewTab({
  user,
  roles,
  extendedUser,
  addresses,
  terms,
}: OverviewTabProps) {
  const meta = extendedUser?.meta_data ?? null;
  const primary = meta?.primary_information ?? {};
  const secondary = meta?.secondary_information ?? {};
  const additional = meta?.additional_information ?? {};
  const preferences = meta?.preferences ?? {};
  const security = meta?.settings?.security ?? {};

  const socialLinks = Object.entries(additional.social_links ?? {}).filter(
    ([, v]) => !!v
  );

  const roleTypes = new Set(roles.map((r) => r.role_type));
  const isStoreOwner = roleTypes.has('store_owner');
  const isMedicalExpert = roleTypes.has('consultant');
  const isIndividual = roleTypes.has('individual');

  return (
    <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
      {/* Left column: account + roles */}
      <div className="space-y-6 lg:col-span-1">
        <Section title="Account">
          <DetailRow label="User ID" value={String(user.id)} />
          <DetailRow label="Email" value={user.email} />
          <DetailRow label="Phone" value={user.phone ?? '—'} />
          <DetailRow
            label="Email verified"
            value={user.is_verified ? 'Yes' : 'No'}
          />
          <DetailRow label="Created" value={formatDate(user.created_at)} />
          <DetailRow label="Updated" value={formatDate(user.updated_at)} />
        </Section>

        <Section title="Roles">
          {roles.length === 0 && (
            <div className="text-sm text-slate-500">No roles assigned.</div>
          )}
          <div className="space-y-2">
            {roles.map((r) => (
              <div
                key={r.id}
                className="flex items-center justify-between gap-2 rounded border border-slate-200 px-3 py-2 text-sm"
              >
                <div>
                  <div className="font-medium capitalize text-slate-800">
                    {r.role_type.replace(/_/g, ' ')}
                  </div>
                  <div className="text-xs text-slate-500">
                    {r.role_verified ? 'Live' : 'Pending verification'}
                  </div>
                </div>
                {r.permissions && r.permissions.length > 0 && (
                  <div className="max-w-[60%] truncate text-right text-xs text-slate-500">
                    {r.permissions.join(', ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* Right column: profile + role-specific details */}
      <div className="space-y-6 lg:col-span-2">
        {secondary.short_bio && (
          <Section title="Bio">
            <p className="whitespace-pre-line text-sm text-slate-700">
              {secondary.short_bio}
            </p>
          </Section>
        )}

        <Section title="Personal information">
          <FieldGrid
            fields={[
              ['Full name', `${user.first_name} ${user.last_name}`.trim()],
              ['Username', secondary.user_name],
              ['Date of birth', primary.date_of_birth],
              ['Middle name', primary.middle_name],
              ['Secondary email', primary.secondary_email],
              ['Secondary phone', primary.secondary_phone],
              [
                'Website',
                secondary.website ? (
                  <a
                    href={secondary.website}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-brand-600 hover:underline"
                  >
                    {secondary.website}
                  </a>
                ) : null,
              ],
              ['Additional info', primary.additional_info],
            ]}
          />
        </Section>

        <Section
          title={`Addresses${addresses.length ? ` (${addresses.length})` : ''}`}
        >
          <AddressList addresses={addresses} />
        </Section>

        <Section title="Terms of service">
          <TermsAcceptance terms={terms} />
        </Section>

        {isStoreOwner && (
          <Section title="Store">
            <FieldGrid
              fields={[
                ['Store name', primary.store_name],
                ['Category', secondary.store_category],
                ['Email', primary.store_email],
                ['Phone', primary.store_phone],
                ['Address', primary.store_address],
                ['Opening hours', secondary.store_opening_hours],
                ['Description', secondary.store_description],
                ['Keywords', formatList(secondary.store_keywords)],
              ]}
            />
          </Section>
        )}

        {isMedicalExpert && (
          <Section title="Practitioner details">
            <FieldGrid
              fields={[
                [
                  'Years of experience',
                  secondary.years_of_experience != null
                    ? String(secondary.years_of_experience)
                    : null,
                ],
                [
                  'Rate per hour',
                  secondary.rate_per_hour != null
                    ? String(secondary.rate_per_hour)
                    : null,
                ],
                ['Education', secondary.education],
                ['Experience summary', secondary.experience_summary],
                ['Certifications', formatList(secondary.certifications)],
                ['Languages', formatList(secondary.languages_spoken)],
              ]}
            />
          </Section>
        )}

        {isIndividual && hasAnyMedicalField(secondary) && (
          <Section title="Health profile">
            <FieldGrid
              fields={[
                [
                  'Height',
                  secondary.height != null ? `${secondary.height} cm` : null,
                ],
                [
                  'Weight',
                  secondary.weight != null ? `${secondary.weight} kg` : null,
                ],
                ['Blood group', secondary.blood_group],
                ['Genotype', secondary.genotype],
                ['Allergies', formatList(secondary.allergies)],
                ['Conditions', formatList(secondary.medical_conditions)],
                ['Medications', formatList(secondary.medications)],
              ]}
            />
            {(secondary.emergency_contact_name ||
              secondary.emergency_contact_phone ||
              secondary.emergency_contact_email) && (
              <div className="mt-4 border-t border-slate-100 pt-3">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Emergency contact
                </h3>
                <div className="mt-2">
                  <FieldGrid
                    fields={[
                      ['Name', secondary.emergency_contact_name],
                      ['Phone', secondary.emergency_contact_phone],
                      ['Email', secondary.emergency_contact_email],
                    ]}
                  />
                </div>
              </div>
            )}
          </Section>
        )}

        {socialLinks.length > 0 && (
          <Section title="Social links">
            <ul className="space-y-1.5 text-sm">
              {socialLinks.map(([platform, url]) => (
                <li
                  key={platform}
                  className="flex items-center justify-between gap-3"
                >
                  <span className="capitalize text-slate-500">{platform}</span>
                  <a
                    href={url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="truncate text-brand-600 hover:underline"
                  >
                    {url}
                  </a>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {(preferences.language || preferences.theme != null) && (
          <Section title="Preferences">
            <FieldGrid
              fields={[
                ['Language', preferences.language],
                ['Theme', preferences.theme],
                [
                  'Newsletter',
                  preferences.news_letter_subscription === true
                    ? 'Subscribed'
                    : preferences.news_letter_subscription === false
                      ? 'Unsubscribed'
                      : null,
                ],
                [
                  '2FA enabled',
                  security.two_factor_authentication === true
                    ? 'Yes'
                    : security.two_factor_authentication === false
                      ? 'No'
                      : null,
                ],
              ]}
            />
          </Section>
        )}

        {!extendedUser && (
          <Section title="Extended profile">
            <div className="text-sm text-slate-500">
              No extended profile information has been submitted.
            </div>
          </Section>
        )}
      </div>
    </div>
  );
}

type Field = [label: string, value: React.ReactNode];

function FieldGrid({ fields }: { fields: Field[] }) {
  const populated = fields.filter(
    ([, v]) => v !== null && v !== undefined && v !== ''
  );
  if (populated.length === 0) {
    return (
      <div className="text-sm text-slate-500">No information provided.</div>
    );
  }
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
      {populated.map(([label, value]) => (
        <div key={label}>
          <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
            {label}
          </dt>
          <dd className="mt-0.5 text-sm text-slate-800">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function formatList(values: (string | number)[] | null | undefined) {
  if (!values || values.length === 0) return null;
  return values.join(', ');
}

function TermsAcceptance({ terms }: { terms: TermsAcceptanceStatus }) {
  if (!terms) {
    return (
      <div className="text-sm text-slate-500">No acceptance recorded.</div>
    );
  }
  if (terms.has_accepted_current) {
    return (
      <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-800">
        <div className="font-medium">
          Accepted current terms ({terms.current_version})
        </div>
        <div className="mt-0.5 text-xs text-emerald-700">
          {formatDate(terms.accepted_at)}
        </div>
      </div>
    );
  }
  if (terms.accepted_version) {
    return (
      <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
        <div className="font-medium">
          Needs to re-accept — on an older version
        </div>
        <div className="mt-0.5 text-xs text-amber-700">
          Accepted {terms.accepted_version} on {formatDate(terms.accepted_at)}.
          Current version is {terms.current_version}.
        </div>
      </div>
    );
  }
  return (
    <div className="rounded border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800">
      <div className="font-medium">Has not accepted any terms</div>
      <div className="mt-0.5 text-xs text-red-700">
        Current version is {terms.current_version}. User must accept before
        transacting.
      </div>
    </div>
  );
}

function AddressList({ addresses }: { addresses: UserAddress[] }) {
  if (addresses.length === 0) {
    return (
      <div className="text-sm text-slate-500">
        This user has not added any addresses.
      </div>
    );
  }
  return (
    <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {addresses.map((a) => (
        <li
          key={a.id}
          className="rounded border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="text-slate-800">{a.address_line_1}</div>
              {a.address_line_2 && (
                <div className="text-slate-600">{a.address_line_2}</div>
              )}
              <div className="text-xs text-slate-500">
                {[a.city, a.state, a.zip_code].filter(Boolean).join(', ')}
                {a.country ? ` · ${a.country}` : ''}
              </div>
              <div className="mt-1 text-[11px] text-slate-400">
                Added {formatDate(a.created_at)}
              </div>
            </div>
            {a.is_default && (
              <span className="shrink-0 rounded-full bg-brand-100 px-2 py-0.5 text-[11px] font-medium text-brand-700">
                Default
              </span>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

function hasAnyMedicalField(
  s: NonNullable<ExtendedUserMetadata['secondary_information']>
): boolean {
  return !!(
    s.height ||
    s.weight ||
    s.blood_group ||
    s.genotype ||
    (s.allergies && s.allergies.length) ||
    (s.medical_conditions && s.medical_conditions.length) ||
    (s.medications && s.medications.length) ||
    s.emergency_contact_name ||
    s.emergency_contact_phone ||
    s.emergency_contact_email
  );
}

// --- Wallet & Payout methods section ---------------------------------------

interface WalletSnapshot {
  has_customer: boolean;
  wallet_id: string | null;
  wallet_status: string | null;
  account_number: string | null;
  bank_name: string | null;
  bank_code: string | null;
  available_balance: number | null;
  ledger_balance: number | null;
}

interface PayoutMethodRow {
  id: number;
  type: 'embedly_wallet' | 'embedly_bank' | 'paystack_bank';
  provider: 'embedly' | 'paystack';
  bank_code: string | null;
  bank_name: string | null;
  account_number: string | null;
  verified_account_name: string | null;
  is_default: boolean;
  is_active: boolean;
  last_used_at: string | null;
}

interface WalletPayoutsResponse {
  wallet: WalletSnapshot | null;
  payout_methods: PayoutMethodRow[];
}

function formatNaira(value: number | null | undefined) {
  if (value == null) return '—';
  if (!Number.isFinite(value)) return String(value);
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    maximumFractionDigits: 2,
  }).format(value);
}

function methodLabel(m: PayoutMethodRow): string {
  if (m.type === 'embedly_wallet') return 'Swiftmedix Wallet';
  const masked = m.account_number ? `****${m.account_number.slice(-4)}` : '';
  const via = m.provider === 'embedly' ? 'via Embedly' : 'via Paystack';
  return `${m.bank_name ?? 'Bank'} ${masked} · ${via}`;
}

function methodFeeLabel(t: PayoutMethodRow['type']): string {
  return t === 'paystack_bank' ? '₦100 / payout' : 'Free';
}

function WalletPayoutsSection({ userId }: { userId: number }) {
  const [data, setData] = useState<WalletPayoutsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get<WalletPayoutsResponse>(
          `/back-office/users/${userId}/wallet`
        );
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled)
          setError(extractErrorMessage(err, 'Failed to load wallet'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  return (
    <section className="mt-6 rounded-lg border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-slate-800">
          Wallet &amp; Payouts
        </h2>
        {data?.wallet?.wallet_status && (
          <span className="rounded bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-slate-600">
            {data.wallet.wallet_status}
          </span>
        )}
      </div>

      {loading && <div className="mt-3 text-sm text-slate-500">Loading…</div>}
      {!loading && error && (
        <div className="mt-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {!loading && !error && data && (
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Embedly wallet */}
          <div className="rounded-md border border-slate-200 p-3">
            <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Swiftmedix Wallet (Embedly)
            </div>
            {!data.wallet?.has_customer ? (
              <div className="mt-2 text-sm text-slate-500">
                Customer hasn’t completed KYC — no wallet provisioned yet.
              </div>
            ) : !data.wallet.wallet_id ? (
              <div className="mt-2 text-sm text-slate-500">
                Wallet not yet provisioned.
              </div>
            ) : (
              <div className="mt-2 space-y-1 text-sm">
                <div className="font-mono text-slate-800">
                  {data.wallet.account_number ?? '—'}
                </div>
                <div className="text-xs text-slate-500">
                  {data.wallet.bank_name ?? '—'}
                  {data.wallet.bank_code && ` · ${data.wallet.bank_code}`}
                </div>
                <div className="pt-2 grid grid-cols-2 gap-2">
                  <div>
                    <div className="text-[10px] uppercase text-slate-500">
                      Available
                    </div>
                    <div className="text-base font-semibold text-emerald-700">
                      {formatNaira(data.wallet.available_balance)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-slate-500">
                      Ledger
                    </div>
                    <div className="text-base font-semibold text-slate-800">
                      {formatNaira(data.wallet.ledger_balance)}
                    </div>
                  </div>
                </div>
                <div className="pt-1 text-[10px] text-slate-400 font-mono truncate">
                  id: {data.wallet.wallet_id}
                </div>
              </div>
            )}
          </div>

          {/* Payout methods */}
          <div className="rounded-md border border-slate-200 p-3">
            <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Payout methods
            </div>
            {data.payout_methods.length === 0 ? (
              <div className="mt-2 text-sm text-slate-500">
                None configured. The seller will use Swiftmedix Wallet by
                default once provisioned.
              </div>
            ) : (
              <ul className="mt-2 space-y-2">
                {data.payout_methods.map((m) => (
                  <li
                    key={m.id}
                    className={
                      'rounded border px-3 py-2 text-sm ' +
                      (m.is_default
                        ? 'border-brand-500 bg-brand-50/40'
                        : 'border-slate-200')
                    }
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-slate-800 truncate">
                        {methodLabel(m)}
                      </span>
                      <span className="text-[10px] text-slate-500 shrink-0">
                        {methodFeeLabel(m.type)}
                      </span>
                    </div>
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-500">
                      {m.is_default && (
                        <span className="rounded bg-brand-500 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-white">
                          Default
                        </span>
                      )}
                      {!m.is_active && (
                        <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-slate-700">
                          Inactive
                        </span>
                      )}
                      {m.verified_account_name && (
                        <span className="truncate">
                          {m.verified_account_name}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
